import { useState, useRef } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

const EXAMPLES = [
  { icon: '🛒', label: 'E-commerce', prompt: 'An e-commerce store with users, products, categories, orders, order items, reviews, and shopping cart' },
  { icon: '📝', label: 'Blog', prompt: 'A blog platform with users, posts, categories, tags, comments, and likes' },
  { icon: '📋', label: 'Project Manager', prompt: 'A project management tool with users, teams, projects, tasks, subtasks, comments, and time tracking' },
  { icon: '🏥', label: 'Healthcare', prompt: 'A healthcare system with patients, doctors, appointments, prescriptions, and medical records' },
  { icon: '🎓', label: 'LMS', prompt: 'A learning management system with students, instructors, courses, lessons, enrollments, quizzes, and grades' },
  { icon: '🏨', label: 'Booking', prompt: 'A hotel booking system with hotels, rooms, guests, reservations, payments, and reviews' },
]

export function SchemaGenPanel({ onClose }: { onClose: () => void }) {
  const [prompt, setPrompt] = useState('')
  const [dbType, setDbType] = useState('postgresql')
  const [includeAPI, setIncludeAPI] = useState(true)
  const [includeAdmin, setIncludeAdmin] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [status, setStatus] = useState('')
  const [files, setFiles] = useState<string[]>([])
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const [streamText, setStreamText] = useState('')
  const logsRef = useRef<HTMLDivElement>(null)

  const generate = async () => {
    if (!prompt.trim() || generating) return
    setGenerating(true); setStatus('Starting...'); setFiles([]); setDone(false); setError(''); setStreamText('')
    try {
      const res = await authFetch(apiUrl('/api/schema-gen'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, dbType, includeAPI, includeAdmin }),
      })
      if (!res.body) { setError('No response'); setGenerating(false); return }
      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''
      while (true) {
        const { done: streamDone, value } = await reader.read()
        if (streamDone) break
        buf += dec.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'status') setStatus(data.text)
            if (data.type === 'chunk') setStreamText(prev => prev + data.text)
            if (data.type === 'file') setFiles(prev => [...prev, data.path])
            if (data.type === 'error') setError(data.text)
            if (data.type === 'done') { setDone(true); setStatus(`✓ Created ${data.fileCount} files in /db-schema`); window.dispatchEvent(new CustomEvent('pulse:files-changed')) }
          } catch {}
        }
      }
    } catch (e: any) { setError(e.message ?? 'Failed') }
    finally { setGenerating(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 540, maxWidth: '90vw', maxHeight: '85vh', background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 16, color: 'var(--text-0)' }}>🗄 Schema Generator</div>
            <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>Describe your data → get schema + API + admin</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {!generating && !done && (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 8 }}>EXAMPLES</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                  {EXAMPLES.map(e => (
                    <button key={e.label} onClick={() => setPrompt(e.prompt)} style={{ padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border)', background: prompt === e.prompt ? 'rgba(124,110,245,0.1)' : 'var(--bg-2)', color: prompt === e.prompt ? 'var(--accent)' : 'var(--text-1)', cursor: 'pointer', fontSize: 9, fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                      <div style={{ fontSize: 16, marginBottom: 2 }}>{e.icon}</div>{e.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 6 }}>DESCRIBE YOUR DATA MODEL</div>
                <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="A social media app with users, posts, comments, likes, followers..." style={{ width: '100%', minHeight: 70, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 12, outline: 'none', resize: 'vertical', lineHeight: 1.5 }} />
              </div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>DATABASE</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {['postgresql', 'mysql', 'sqlite'].map(d => (
                      <button key={d} onClick={() => setDbType(d)} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 10, fontFamily: 'var(--font-mono)', border: dbType === d ? '1px solid var(--accent)' : '1px solid var(--border-2)', background: dbType === d ? 'rgba(124,110,245,0.1)' : 'none', color: dbType === d ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer' }}>{d}</button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', cursor: 'pointer' }}><input type="checkbox" checked={includeAPI} onChange={e => setIncludeAPI(e.target.checked)} /> API Routes</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', cursor: 'pointer' }}><input type="checkbox" checked={includeAdmin} onChange={e => setIncludeAdmin(e.target.checked)} /> Admin UI</label>
                </div>
              </div>
            </>
          )}
          {generating && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><span style={{ animation: 'pulse-heartbeat 1.4s infinite', fontSize: 12 }}>🗄</span><span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700 }}>{status}</span></div>
              {files.length > 0 && <div style={{ marginBottom: 12 }}>{files.map(f => <div key={f} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--accent-2)', padding: '2px 0' }}>📄 {f}</div>)}</div>}
              <div style={{ background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, maxHeight: 200, overflow: 'auto', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{streamText.slice(-2000)}<div ref={logsRef} /></div>
            </div>
          )}
          {done && (
            <div>
              <div style={{ background: 'rgba(95,212,164,0.08)', border: '1px solid rgba(95,212,164,0.3)', borderRadius: 10, padding: 16, marginBottom: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>🗄</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--accent-2)', marginBottom: 4 }}>{status}</div>
              </div>
              {files.map(f => <div key={f} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--accent-2)', padding: '2px 0' }}>📄 {f}</div>)}
            </div>
          )}
          {error && <div style={{ color: 'var(--red)', fontSize: 11, fontFamily: 'var(--font-mono)', marginTop: 8 }}>⚠️ {error}</div>}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          {!generating && !done && (
            <button onClick={generate} disabled={!prompt.trim()} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: prompt.trim() ? 'var(--accent)' : 'var(--bg-3)', color: prompt.trim() ? '#fff' : 'var(--text-2)', cursor: prompt.trim() ? 'pointer' : 'not-allowed', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12 }}>🗄 Generate Database</button>
          )}
          {done && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setDone(false); setPrompt(''); setFiles([]); setStreamText('') }} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>Generate Another</button>
              <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: 'var(--accent-2)', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>Open Files</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
